import { useMemo, useRef, useState } from "react";
import type { NovelListSource, NovelSummary } from "../apiClient.js";
import type { Page } from "../types.js";
import { sourceLabel } from "../lib/novel.js";
import { FilmCard } from "../components/FilmCard.js";
import { ContinueReadingShelf } from "../components/ContinueReadingShelf.js";
import { LibraryTabs } from "../components/LibraryTabs.js";
import { useLanguage, useT } from "../lib/i18n/store.js";
import { NovelListMore, type NovelListPagination } from "../components/NovelListMore.js";
import { ReelSelect } from "../components/ReelSelect.js";
import { CatalogFilterBar } from "../components/CatalogFilterBar.js";
import { track } from "../lib/analytics.js";
import { NO_CATALOG_FILTERS, hasCatalogFilters, type CatalogFilters } from "../lib/catalogFilters.js";
import {
  CATALOG_SORT_OPTIONS,
  sortNovels,
  useFullListForSort,
  type NovelSortKey
} from "../lib/novelSort.js";

export function CatalogPage({
  pagination,
  novels,
  source,
  loading,
  filters = NO_CATALOG_FILTERS,
  onFiltersChange,
  facetsReady = true,
  facetsReloadKey = 0,
  onRetry,
  onNavigate,
  onPlay,
  onOpenDetail,
  onOpenAuthor,
  onCreate
}: {
  pagination?: NovelListPagination;
  /** The rows to show: the whole catalog, or — with `filters` set — only the matches. */
  novels: NovelSummary[];
  source: NovelListSource;
  loading: boolean;
  /** The language / genre filters in effect (App keeps them in the URL). */
  filters?: CatalogFilters;
  /** Shows the filter bar; called with the next filters when a chip is chosen. */
  onFiltersChange?: (filters: CatalogFilters) => void;
  /** The filter bar may read the facets now (App's gate for a signed-out catalog read). Default: yes. */
  facetsReady?: boolean;
  /** Changes when the API comes back after being down: the bar asks for the facets again. */
  facetsReloadKey?: number;
  /** Reloads a filtered list that could not be read. */
  onRetry?: () => void;
  onNavigate: (page: Page) => void;
  onPlay: (novelId: string) => void;
  onOpenDetail: (novelId: string) => void;
  onOpenAuthor: (ownerId: string, username?: string) => void;
  onCreate: () => void;
}) {
  const t = useT();
  const lang = useLanguage();
  // "recommended" keeps the server's ranked feed order, page by page; any other
  // order sorts the whole catalog, so it loads the remaining pages first.
  const [sort, setSort] = useState<NovelSortKey>("recommended");
  const sortPending = useFullListForSort(sort, "recommended", pagination);
  const sortedNovels = useMemo(() => sortNovels(novels, sort), [novels, sort]);
  const showSkeleton = loading || sortPending;
  const filtered = hasCatalogFilters(filters);
  // A filtered read that got no answer shows nothing (never the demo novel) and
  // says so below; the status line must not name a demo that is not there.
  const filteredUnavailable = filtered && source === "fallback";
  // Nothing matched: said in the status line too — a live region that was on
  // the page before its text changed, so a screen-reader user hears the result
  // of pressing a chip (an empty state that appears already filled is not read).
  const filteredNoMatch = filtered && !loading && novels.length === 0 && source !== "fallback";
  // The empty state's buttons vanish when pressed (the grid or the skeleton
  // takes their place), which would drop keyboard focus to <body>: move it to
  // the filter bar, or to the heading when the bar has nothing to show.
  const filterBarRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  function keepFocus() {
    (filterBarRef.current ?? headingRef.current)?.focus();
  }
  // The empty state's "show all": one event per filter it clears.
  function clearFilters() {
    if (!onFiltersChange) return;
    keepFocus();
    if (filters.language !== null) track("catalog_filter_changed", { filter: "language", value: null });
    if (filters.genre !== null) track("catalog_filter_changed", { filter: "genre", value: null });
    onFiltersChange(NO_CATALOG_FILTERS);
  }
  return (
    <section className="page-section">
      <LibraryTabs active="catalog" onNavigate={onNavigate} />
      <div className="section-heading">
        <div className="catalog-heading-row">
          <h1 ref={headingRef} tabIndex={-1}>
            {t("catalog.heading")}
          </h1>
          {!loading && novels.length > 1 && (
            <div className="reel-toolbar-controls">
              <span className="reel-toolbar-label" aria-hidden="true">
                {t("author.sortLabel")}
              </span>
              <ReelSelect
                value={sort}
                options={CATALOG_SORT_OPTIONS.map((option) => ({ key: option.key, label: t(option.labelKey) }))}
                onChange={setSort}
                label={t("author.sortLabel")}
              />
            </div>
          )}
        </div>
        {/* One AI notice for the whole grid, where the covers are first seen —
            instead of an "AI" chip on every card. */}
        <p className="catalog-ai-note">{t("ai.siteNotice")}</p>
        {/* The status line lives OUTSIDE the aria-busy region below — AT may
            withhold changes inside a busy subtree, and this line is the
            accessible loading signal (the skeletons are aria-hidden). It is
            only SHOWN when something is off (a cached copy or the demo
            fallback); the healthy "live" state stays for assistive tech only,
            so public visitors don't see a developer-facing status line. */}
        <p className={loading || source === "api" || filteredUnavailable ? "metrics sr-only" : "metrics"} role="status">
          Source: {sourceLabel(source, loading, filteredUnavailable ? "unavailable" : "demo fallback")}
          {filteredNoMatch && `. ${t("catalog.filterEmptyTitle")}`}
        </p>
      </div>
      {onFiltersChange && (
        <CatalogFilterBar
          ref={filterBarRef}
          filters={filters}
          onChange={onFiltersChange}
          enabled={facetsReady}
          reloadKey={facetsReloadKey}
        />
      )}
      {/* A returning reader's unfinished stories, above the whole catalog (a
          filtered view shows just the matches). */}
      {!loading && !filtered && <ContinueReadingShelf novels={novels} onPlay={onPlay} className="catalog-continue" />}
      <div aria-busy={showSkeleton}>
      {showSkeleton ? (
        <div className="film-grid" data-testid="catalog-skeleton" aria-hidden="true">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="film-card film-card--skeleton skeleton" />
          ))}
        </div>
      ) : filtered && novels.length === 0 ? (
        source === "fallback" ? (
          <div className="empty-state catalog-filter-empty" role="alert">
            <p className="empty-state-message">{t("catalog.filterLoadFailed")}</p>
            {onRetry && (
              <button
                type="button"
                className="empty-state-cta"
                onClick={() => {
                  keepFocus();
                  onRetry();
                }}
              >
                {t("catalog.filterRetry")}
              </button>
            )}
          </div>
        ) : (
          <div className="empty-state catalog-filter-empty">
            <p className="empty-state-message">{t("catalog.filterEmptyTitle")}</p>
            <p>{t("catalog.filterEmptyText")}</p>
            {onFiltersChange && (
              <button type="button" className="empty-state-cta" onClick={clearFilters}>
                {t("catalog.filterClear")}
              </button>
            )}
          </div>
        )
      ) : novels.length === 0 ? (
        <div className="empty-state">
          <img src="/assets/empty-state.png" alt="" className="empty-state-art" />
          <p className="empty-state-message">{t("catalog.emptyTitle")}</p>
          <p>{t("catalog.emptyText")}</p>
          <button type="button" className="empty-state-cta" onClick={onCreate}>
            {t("common.createNovel")}
          </button>
        </div>
      ) : (
        <div className="film-grid">
          {sortedNovels.map((novel) => (
            <FilmCard
              key={novel.id}
              novel={novel}
              lang={lang}
              t={t}
              onPlay={onPlay}
              onOpenDetail={onOpenDetail}
              onOpenAuthor={onOpenAuthor}
            />
          ))}
        </div>
      )}
      </div>
      {!showSkeleton && <NovelListMore pagination={pagination} />}
    </section>
  );
}
